import { useMemo } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';

import { extractEntityTargets, loadEntitySnapshotsForTargets } from '../lib/entitySnapshots';

// Resolves the entity hashids embedded in `html` against Dexie for the given workspace, returning
// the snapshot shape that RichdownContent/useEntityReplacer consume:
//   { profile?: { [id]: snapshot|null }, channel?: {...}, emoji?: {...} }
//
// Why this hook (and not useContext(EntitySnapshotContext)) for non-chat surfaces:
//
//   1. Tree position. Some callers — notably <ActivityFeed> in the workspace rail
//      (MainLayout.jsx) — render as siblings of the EntitySnapshotContext.Provider, not
//      children, so useContext(EntitySnapshotContext) returns null there.
//   2. Scope mismatch. EntitySnapshotContext is built for the currently-routed workspace
//      only. The activity feed is cross-workspace by design: a row from workspace X
//      references hashids that only resolve against X's Dexie indexes, so a single-
//      workspace Map could never cover every row. Search uses the same hook to stay
//      consistent with that pattern, even though search rows are single-workspace today.
//
// Each row sources its own snapshots from Dexie scoped to its own workspace_id.
export default function useEntitySnapshotsForText(workspaceId, html) {
  const targets = useMemo(() => {
    if (!html) return { profile: [], channel: [], emoji: [] };
    const extracted = extractEntityTargets(html);
    return {
      profile: extracted.profile.slice().sort(),
      channel: extracted.channel.slice().sort(),
      emoji: extracted.emoji.slice().sort(),
    };
  }, [html]);

  const cacheKey = useMemo(
    () => `${workspaceId || ''}|${targets.profile.join(',')}|${targets.channel.join(',')}|${targets.emoji.join(',')}`,
    [workspaceId, targets],
  );

  return useLiveQuery(() => loadEntitySnapshotsForTargets(workspaceId, targets), [cacheKey], {});
}
